// src/components/Quiz.js (Final Fixed Version - No Warnings)
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Global.css";
import { generateOneEssay, refreshEssayAnswer } from "../essayGenerator";
import { polishEssayWithGemini, getGeminiKey } from "../geminiEssay";

const countryMenu = {
  France: ["Bordeaux", "Burgundy", "Loire", "Rhone", "Alsace", "Jura", "Southern France"],
  Germany: [],
  Australia: [],
  NewZealand: [],
  Spain: [],
  Portugal: [],
  USA: ["California"],
  SouthAfrica: [],
  Italy: ["Northern East", "Central Italy", "Southern Italy"],
  Chile: [],
  Argentina: [],
  Greece: [],
};

const grapeMenu = {
  Chardonnay: [],
  "Pinot Noir": [],
  "Cabernet Sauvignon": [],
  Merlot: [],
  Grenache: [],
  Malbec: [],
  Syrah: [],
  Riesling: [],
  "Sauvignon Blanc": [],
  "Pinot Grigio": [],
  "Cabernet Franc": [],
};

const questionFiles = {
  France: {
    Bordeaux: require("../data/France/Bordeaux.json"),
    Burgundy: require("../data/France/Burgundy.json"),
    Loire: require("../data/France/Loire.json"),
    Rhone: require("../data/France/Rhone.json"),
    Alsace: require("../data/France/Alsace.json"),
    Jura: require("../data/France/Jura.json"),
    "Southern France": require("../data/France/Southern France.json"),
  },
  Germany: require("../data/Germany.json"),
  Spain: require("../data/Spain.json"),
  Portugal: require("../data/Portugal.json"),
  USA: { California: require("../data/USA/California.json") },
  Italy: {
    "Northern East": require("../data/Italy/Northern East.json"),
    "Central Italy": require("../data/Italy/Central Italy.json"),
    "Southern Italy": require("../data/Italy/Southern Italy.json"),
  },
  SouthAfrica: require("../data/South Africa.json"),
  Australia: require("../data/Australia.json"),
  NewZealand: require("../data/New Zealand.json"),
  Chile: require("../data/Chile.json"),
  Argentina: require("../data/Argentina.json"),
  Greece: require("../data/Greece.json"),
  Grapes: {
    Chardonnay: require("../data/Grapes/Chardonnay.json"),
    "Pinot Noir": require("../data/Grapes/Pinot Noir.json"),
    "Cabernet Sauvignon": require("../data/Grapes/Cabernet Sauvignon.json"),
    Merlot: require("../data/Grapes/Merlot.json"),
    Grenache: require("../data/Grapes/Grenache.json"),
    Malbec: require("../data/Grapes/Malbec.json"),
    "Syrah": require("../data/Grapes/Syrah.json"),
    "Riesling": require("../data/Grapes/Riesling.json"),
    "Sauvignon Blanc": require("../data/Grapes/Sauvignon Blanc.json"),
    "Pinot Grigio": require("../data/Grapes/Pinot Grigio.json"),
    "Cabernet Franc": require("../data/Grapes/Cabernet Franc.json"),
  },
};

const letters = ["A", "B", "C", "D", "E", "F"];
const LEARNED_LIMIT = 10;

// ==============================================
// Similarity engine (TF-IDF + cosine similarity)
// ==============================================
// Common English words that carry little meaning for matching wine questions.
const STOPWORDS = new Set([
  "the", "a", "an", "of", "to", "in", "is", "are", "which", "what", "why", "how",
  "for", "and", "or", "from", "with", "this", "that", "these", "those", "into",
  "as", "its", "it", "be", "by", "on", "at", "does", "do", "did", "than", "then",
  "because", "often", "only", "most", "more", "general", "generally", "primarily",
  "traditionally", "largely", "mainly", "usually", "allow", "allows", "allowed",
  "permitted", "permit", "sole", "responsible", "characteristic", "following",
  "one", "two", "their", "they", "has", "have", "was", "were", "can", "may",
  "such", "used", "use", "uses", "known", "main", "type", "types", "wine", "wines",
  "grape", "grapes", "variety", "varieties", "region", "regions", "produce",
  "produced", "production", "make", "made", "making",
]);

const tokenize = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w));

// Build a token bag from the question text plus all answer texts.
const docTokens = (q) => {
  const parts = [q?.question?.en || ""];
  (q?.answers || []).forEach((a) => parts.push(a?.text?.en || ""));
  return tokenize(parts.join(" "));
};

// Flatten every question across all countries, regions, and grapes.
const collectAllQuestions = () => {
  const result = [];
  const seen = new Set();
  const walk = (node) => {
    if (Array.isArray(node)) {
      node.forEach((q) => {
        const id = q?.question?.en;
        if (id && !seen.has(id)) {
          seen.add(id);
          result.push(q);
        }
      });
    } else if (node && typeof node === "object") {
      Object.values(node).forEach(walk);
    }
  };
  walk(questionFiles);
  return result;
};

// Precompute a TF-IDF vector for every question once at module load.
const buildCorpus = () => {
  const questions = collectAllQuestions();
  const docs = questions.map((q) => {
    const tokens = docTokens(q);
    const tf = {};
    tokens.forEach((t) => (tf[t] = (tf[t] || 0) + 1));
    return { q, tf };
  });

  const df = {};
  docs.forEach((d) => {
    Object.keys(d.tf).forEach((t) => (df[t] = (df[t] || 0) + 1));
  });

  const N = docs.length || 1;
  const idf = {};
  Object.keys(df).forEach((t) => (idf[t] = Math.log(N / df[t]) + 1));

  docs.forEach((d) => {
    const vec = {};
    let norm = 0;
    Object.entries(d.tf).forEach(([t, f]) => {
      const w = f * (idf[t] || 0);
      vec[t] = w;
      norm += w * w;
    });
    d.vec = vec;
    d.norm = Math.sqrt(norm) || 1;
  });

  return docs;
};

const CORPUS = buildCorpus();

// Return the top-N most similar questions to the given one via cosine similarity.
const findSimilarQuestions = (targetQuestion, topN = 5) => {
  const targetId = targetQuestion?.question?.en;
  const target = CORPUS.find((d) => d.q.question?.en === targetId);
  if (!target) return [];

  return CORPUS
    .filter((d) => d.q.question?.en !== targetId)
    .map((d) => {
      let dot = 0;
      Object.entries(target.vec).forEach(([t, w]) => {
        if (d.vec[t]) dot += w * d.vec[t];
      });
      return { q: d.q, sim: dot / (target.norm * d.norm) };
    })
    .filter((s) => s.sim > 0)
    .sort((a, b) => b.sim - a.sim)
    .slice(0, topN);
};

const Quiz = ({ username, setUsername, setIsLoggedIn }) => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState("en");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [viewAnswerVisible, setViewAnswerVisible] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [mistakes, setMistakes] = useState([]);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [previousQuestionState, setPreviousQuestionState] = useState(null);
  const [starredQuestions, setStarredQuestions] = useState([]);
  const [reviewMode, setReviewMode] = useState(null);
  const [similarQuestions, setSimilarQuestions] = useState(null);
  const [essayDrafts, setEssayDrafts] = useState({});
  const [learnedNeedsCull, setLearnedNeedsCull] = useState(false);
  const [learnedMasteredIds, setLearnedMasteredIds] = useState([]);
  const [learnedMasteredSources, setLearnedMasteredSources] = useState([]);
  const [generatingEssay, setGeneratingEssay] = useState(false);
  const [geminiError, setGeminiError] = useState("");
  const geminiConnected = Boolean(getGeminiKey());

  // ==============================================
  // 1. Save Progress (DEFINED FIRST - Fixes "used before defined" warning)
  // ==============================================
  // ==============================================
// 1. Save Progress (仅修改存储逻辑，不删原有代码)
// ==============================================
const saveProgress = useCallback((progressData) => {
  // Skip if no username or guest
  if (!username || username === "guest") {
    if (username === "guest") {
      localStorage.removeItem(`progress_${username}`);
      localStorage.removeItem(`starred_${username}`);
      localStorage.removeItem(`mistakes_${username}`);
      setQuestionsAnswered(0);
      setTotalQuestions(0);
    }
    return;
  }

  // 仅保存进度数据（错题/收藏已在各自逻辑里实时保存，避免覆盖）
  localStorage.setItem(`progress_${username}`, JSON.stringify(progressData));
  
  // 注释/删除以下两行（避免读取旧数据再写回，覆盖toggleStar/selectAnswer里的最新数据）
  // const latestStarred = JSON.parse(localStorage.getItem(`starred_${username}`) || "[]");
  // const latestMistakes = JSON.parse(localStorage.getItem(`mistakes_${username}`) || "[]");
  // localStorage.setItem(`starred_${username}`, JSON.stringify(latestStarred));
  // localStorage.setItem(`mistakes_${username}`, JSON.stringify(latestMistakes));
}, [username]);

const persistLearnedSet = useCallback((payload) => {
  if (!username) return;
  localStorage.setItem(`learnedSet_${username}`, JSON.stringify(payload));
}, [username]);

const createLearnedEssay = useCallback(async (existing = [], masteredIds = [], masteredSources = []) => {
  const draft = generateOneEssay(questionFiles, {
    mistakes,
    starredIds: starredQuestions,
    excludeIds: [...existing.map((q) => q.id), ...masteredIds],
    excludeSourceEns: [
      ...existing.flatMap((q) => q.sourceQuestionEns || []),
      ...masteredSources,
    ],
  }) || generateOneEssay(questionFiles, {
    mistakes,
    starredIds: starredQuestions,
    excludeIds: existing.map((q) => q.id),
    excludeSourceEns: existing.flatMap((q) => q.sourceQuestionEns || []),
  });
  if (!draft) return null;
  if (!getGeminiKey()) return draft;
  try {
    return await polishEssayWithGemini(draft, questionFiles);
  } catch (err) {
    console.error("Gemini essay error:", err);
    setGeminiError(err.message || "Gemini request failed");
    return draft;
  }
}, [mistakes, starredQuestions]);

  // ==============================================
  // 2. Load User Data (Uses saveProgress AFTER it's defined)
  // ==============================================
  useEffect(() => {
    if (username === "guest") {
      setStarredQuestions([]);
      setMistakes([]);
      setQuestionsAnswered(0);
      setTotalQuestions(0);
      return;
    }

    try {
      const savedStarred = JSON.parse(localStorage.getItem(`starred_${username}`) || "[]");
      const savedMistakes = JSON.parse(localStorage.getItem(`mistakes_${username}`) || "[]");
      const savedProgress = JSON.parse(localStorage.getItem(`progress_${username}`) || "{}");

      setStarredQuestions(savedStarred);
      setMistakes(savedMistakes);
      setQuestionsAnswered(savedProgress.answered || 0);
      setTotalQuestions(savedProgress.total || 0);

      // Now safe to use saveProgress (defined above)
      saveProgress({
        answered: savedProgress.answered || 0,
        total: savedProgress.total || 0
      });
    } catch (e) {
      console.error("加载用户数据失败", e);
      setStarredQuestions([]);
      setMistakes([]);
    }
  }, [username, saveProgress]);

  // ==============================================
  // 3. Auto-Save Progress
  // ==============================================
  useEffect(() => {
    // Skip if no username, guest, or empty progress
    if (!username || username === "guest" || (questionsAnswered === 0 && totalQuestions === 0)) {
      return;
    }

    // Auto-save only when values change
    saveProgress({
      answered: questionsAnswered,
      total: totalQuestions
    });
  }, [username, questionsAnswered, totalQuestions, saveProgress]);

  // ==============================================
  // 4. Load Questions
  // ==============================================
  useEffect(() => {
    if (selectedCategory !== "learned") {
      setLearnedNeedsCull(false);
      setGeneratingEssay(false);
      return;
    }

    let cancelled = false;

    const startLearned = async () => {
      try {
        const saved = JSON.parse(localStorage.getItem(`learnedSet_${username}`) || "null");
        if (saved?.questions?.length) {
          const trimmed = saved.questions
            .slice(0, LEARNED_LIMIT)
            .map((q) => refreshEssayAnswer(q, questionFiles));
          const current = trimmed.find((q) => q.id === saved.currentId) || trimmed[0];
          const masteredIds = saved.masteredIds || [];
          const masteredSources = saved.masteredSources || [];
          if (cancelled) return;
          setQuestions(trimmed);
          setCurrentQuestion(current);
          setLearnedMasteredIds(masteredIds);
          setLearnedMasteredSources(masteredSources);
          setLearnedNeedsCull(false);
          setSelectedAnswer(null);
          setIsFlipped(false);
          setEssayDrafts({});
          persistLearnedSet({
            questions: trimmed,
            currentId: current?.id || null,
            masteredIds,
            masteredSources,
          });
          return;
        }
      } catch (e) {
        console.error("加载简答题失败", e);
      }

      setGeneratingEssay(true);
      const first = await createLearnedEssay([], [], []);
      if (cancelled) return;
      const list = first ? [first] : [];
      setQuestions(list);
      setCurrentQuestion(list[0] || null);
      setLearnedMasteredIds([]);
      setLearnedMasteredSources([]);
      setLearnedNeedsCull(false);
      setSelectedAnswer(null);
      setIsFlipped(false);
      setEssayDrafts({});
      setGeneratingEssay(false);
      if (list[0]) {
        persistLearnedSet({
          questions: list,
          currentId: list[0].id,
          masteredIds: [],
          masteredSources: [],
        });
      }
    };

    startLearned();
    return () => {
      cancelled = true;
    };
    // Only re-run when entering this section or switching user — not when mistakes change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, username]);

  useEffect(() => {
    if (selectedCategory === "learned") return;
    if (!selectedCountry) return;

    let questionsArray;
    if (selectedCategory === "country") {
      if (selectedRegion) {
        questionsArray = questionFiles[selectedCountry]?.[selectedRegion];
      } else {
        questionsArray = questionFiles[selectedCountry];
        if (!Array.isArray(questionsArray)) {
          questionsArray = Object.values(questionsArray).flat();
        }
      }
    } else if (selectedCategory === "grape") {
      questionsArray = questionFiles.Grapes[selectedCountry];
    }

    if (!questionsArray || questionsArray.length === 0) {
      setQuestions([]);
      setCurrentQuestion(null);
      setTotalQuestions(0);
      setQuestionsAnswered(0);
      return;
    }

    const shuffled = questionsArray
      .map((q) => ({ ...q, answers: [...q.answers].sort(() => Math.random() - 0.5) }))
      .sort(() => Math.random() - 0.5);

    setQuestions(shuffled);
    setCurrentQuestion(shuffled[0]);
    setTotalQuestions(shuffled.length);
    setQuestionsAnswered(0);
    setSelectedAnswer(null);
    setViewAnswerVisible(false);
    setIsFlipped(false);
    setQuestionHistory([]);
  }, [selectedCountry, selectedRegion, selectedCategory]);

  // ==============================================
  // 5. Toggle Star
  // ==============================================
  const toggleStar = (q) => {
    if (username === "guest" || !q?.question?.en) return;
    const questionId = q.question.en;

    setStarredQuestions((prev) => {
      const updated = prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId];
      
      // Save immediately to localStorage (sync with Google login)
      localStorage.setItem(`starred_${username}`, JSON.stringify(updated));
      return updated;
    });

    // Trigger saveProgress to sync progress data
    saveProgress({
      answered: questionsAnswered,
      total: totalQuestions
    });
  };

  // ==============================================
  // 6. Select Answer
  // ==============================================
  const selectAnswer = (answer) => {
    setSelectedAnswer(answer);
    setViewAnswerVisible(true);

    if (!answer.correct && username !== "guest") {
      const questionId = currentQuestion.question.en;
      const isDuplicate = mistakes.some(
        (m) => m.question.question.en === questionId && m.selectedAnswer.text.en === answer.text.en
      );
      if (!isDuplicate) {
        const newMistake = { question: currentQuestion, selectedAnswer: answer };
        setMistakes((prev) => {
          const updated = [...prev, newMistake];
          // Save immediately to localStorage (sync with Google login)
          localStorage.setItem(`mistakes_${username}`, JSON.stringify(updated));
          return updated;
        });

        // Trigger saveProgress to sync progress data
        saveProgress({
          answered: questionsAnswered,
          total: totalQuestions
        });
      }
    }
  };

  // ==============================================
  // 6b. Remove Mistake
  // ==============================================
  const removeMistake = (index) => {
    setMistakes((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Save immediately to localStorage (sync with Google login)
      if (username && username !== "guest") {
        localStorage.setItem(`mistakes_${username}`, JSON.stringify(updated));
      }
      // Exit review if no mistakes remain
      if (updated.length === 0) {
        setReviewMode(null);
      }
      return updated;
    });
  };

  // ==============================================
  // 7. Next Question
  // ==============================================
  const persistCurrentLearned = (list, current, masteredIds = learnedMasteredIds, masteredSources = learnedMasteredSources) => {
    persistLearnedSet({
      questions: list,
      currentId: current?.id || null,
      masteredIds,
      masteredSources,
    });
  };

  const nextQuestion = async () => {
    if (currentQuestion?.type === "essay") {
      if (!isFlipped || generatingEssay) return;
      const idx = questions.findIndex((q) => q.id === currentQuestion.id);
      if (idx >= 0 && idx < questions.length - 1) {
        const next = questions[idx + 1];
        setCurrentQuestion(next);
        setIsFlipped(false);
        persistCurrentLearned(questions, next);
        return;
      }
      if (questions.length >= LEARNED_LIMIT) {
        setLearnedNeedsCull(true);
        return;
      }
      setGeneratingEssay(true);
      const generated = await createLearnedEssay(questions, learnedMasteredIds, learnedMasteredSources);
      setGeneratingEssay(false);
      if (!generated) {
        setLearnedNeedsCull(true);
        return;
      }
      const updated = [...questions, generated];
      setQuestions(updated);
      setCurrentQuestion(generated);
      setIsFlipped(false);
      persistCurrentLearned(updated, generated);
      return;
    }

    if (!selectedAnswer || !currentQuestion) return;
    setQuestionHistory((prev) => [...prev, { question: currentQuestion, selectedAnswer }]);

    let remaining = questions.filter((q) => q !== currentQuestion);
    if (selectedAnswer?.correct && username !== "guest") {
      setQuestionsAnswered((prev) => prev + 1);
    } else {
      remaining.push(currentQuestion);
    }

    if (remaining.length === 0) {
      setCurrentQuestion(null);
    } else {
      setCurrentQuestion(remaining[Math.floor(Math.random() * remaining.length)]);
    }

    setQuestions(remaining);
    setSelectedAnswer(null);
    setIsFlipped(false);
    setViewAnswerVisible(false);
  };

  const previousQuestion = () => {
    if (currentQuestion?.type === "essay") {
      const idx = questions.findIndex((q) => q.id === currentQuestion.id);
      if (idx > 0) {
        const prev = questions[idx - 1];
        setCurrentQuestion(prev);
        setIsFlipped(false);
        persistCurrentLearned(questions, prev);
      }
      return;
    }

    if (questionHistory.length === 0) return;
    const last = questionHistory[questionHistory.length - 1];
    setCurrentQuestion(last.question);
    setSelectedAnswer(last.selectedAnswer);
    setIsFlipped(true);
    setViewAnswerVisible(false);
    setQuestionHistory((prev) => prev.slice(0, prev.length - 1));
  };

  const removeLearnedQuestion = async (id) => {
    const removed = questions.find((q) => q.id === id);
    const updated = questions.filter((q) => q.id !== id);
    const masteredIds = removed ? [...learnedMasteredIds, removed.id] : learnedMasteredIds;
    const masteredSources = removed
      ? [...learnedMasteredSources, ...(removed.sourceQuestionEns || [])]
      : learnedMasteredSources;
    setLearnedMasteredIds(masteredIds);
    setLearnedMasteredSources(masteredSources);
    setLearnedNeedsCull(false);

    let nextCurrent = currentQuestion;
    if (!updated.length) {
      setGeneratingEssay(true);
      const generated = await createLearnedEssay([], masteredIds, masteredSources);
      setGeneratingEssay(false);
      if (generated) {
        updated.push(generated);
        nextCurrent = generated;
      } else {
        nextCurrent = null;
      }
    } else if (currentQuestion?.id === id) {
      const idx = questions.findIndex((q) => q.id === id);
      nextCurrent = updated[Math.min(idx, updated.length - 1)] || updated[0];
    }

    setQuestions(updated);
    setCurrentQuestion(nextCurrent);
    setIsFlipped(false);
    persistCurrentLearned(updated, nextCurrent, masteredIds, masteredSources);
  };

  // ==============================================
  // 9. Start Review Mode
  // ==============================================
  const startReview = (mode) => {
    setPreviousQuestionState({
      selectedCategory,
      selectedCountry,
      selectedRegion,
      questions,
      currentQuestion,
      selectedAnswer,
      isFlipped,
      viewAnswerVisible,
    });
    setReviewMode(mode);
    setSelectedCategory(null);
    setSelectedCountry(null);
    setSelectedRegion(null);
    setSelectedAnswer(null);
    setIsFlipped(false);
    setViewAnswerVisible(false);
  };

  // ==============================================
  // 10. Exit Review Mode
  // ==============================================
  const exitReview = () => {
    if (previousQuestionState) {
      setSelectedCategory(previousQuestionState.selectedCategory);
      setSelectedCountry(previousQuestionState.selectedCountry);
      setSelectedRegion(previousQuestionState.selectedRegion);
      setQuestions(previousQuestionState.questions);
      setCurrentQuestion(previousQuestionState.currentQuestion);
      setSelectedAnswer(previousQuestionState.selectedAnswer);
      setIsFlipped(previousQuestionState.isFlipped);
      setViewAnswerVisible(previousQuestionState.viewAnswerVisible);
      setPreviousQuestionState(null);
    }
    setReviewMode(null);
  };

  // ==============================================
  // 11. Back to Menu
  // ==============================================
  const backToMenu = () => {
    setSelectedCategory(null);
    setSelectedCountry(null);
    setSelectedRegion(null);
    setQuestions([]);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setIsFlipped(false);
    setViewAnswerVisible(false);
    setReviewMode(null);
    setQuestionHistory([]);
    setEssayDrafts({});
    setLearnedNeedsCull(false);
  };

  // ==============================================
  // 12. Logout
  // ==============================================
  // ==============================================
// 12. Logout (仅修改这部分，删除数据的代码注释掉)
// ==============================================
const handleLogout = () => {
  try {
    // 注释/删除以下三行删除数据的代码 → 保留用户进度
    // localStorage.removeItem(`progress_${username}`);
    // localStorage.removeItem(`starred_${username}`);
    // localStorage.removeItem(`mistakes_${username}`);
    
    localStorage.removeItem("quizUsername"); // 仅删除登录态，不删数据
    setUsername("");
    setIsLoggedIn(false);
    navigate("/login");
  } catch (error) {
    console.error("Logout error:", error);
  }
};

  // ==============================================
  // Helper Functions
  // ==============================================
  const handleViewAnswer = () => {
    if (currentQuestion?.type !== "essay" && !selectedAnswer) return;
    setIsFlipped(true);
    setViewAnswerVisible(false);
  };

  const handleFindSimilar = (q) => {
    if (!q?.question?.en) return;
    setSimilarQuestions(findSimilarQuestions(q, 5));
  };

  const progressPercentage = totalQuestions === 0 ? 0 : Math.round((questionsAnswered / totalQuestions) * 100);
  const t = (en, cn) => (language === "en" ? en : cn);
  const isStarred = (q) => starredQuestions.includes(q?.question?.en);

  // ==============================================
  // Render
  // ==============================================
  return (
    <div className="min-h-screen p-4 bg-gradient-to-b from-rose-50 to-rose-100">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-serif" style={{ color: "#B44A6F" }}>🍷 Wine Flashcards</h1>
            <div className="text-sm text-gray-500">
              Hi, <span className="font-medium">{username || "Guest"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setLanguage(language === "en" ? "cn" : "en")} className="px-3 py-1 rounded-md border">
              {language === "en" ? "中文" : "EN"}
            </button>
            <button onClick={handleLogout} className="px-3 py-1 rounded-md bg-rose-400 text-white">Logout</button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-3 mb-4">
          {!selectedCategory && !reviewMode && (
            <>
              <button onClick={() => setSelectedCategory("country")} className="px-4 py-2 rounded-full bg-pink-300 text-white">
                {t("By Country", "按国家")}
              </button>
              <button onClick={() => setSelectedCategory("grape")} className="px-4 py-2 rounded-full bg-pink-300 text-white">
                {t("By Grape", "按葡萄品种")}
              </button>
              <button
                onClick={() => setSelectedCategory("learned")}
                className="px-4 py-2 rounded-full text-white"
                style={{ background: "#C86B85" }}
              >
                {t("Test How Much I Learned", "检验学习成果")}
              </button>
            </>
          )}

          {username && username !== "guest" && mistakes.length > 0 && !reviewMode && (
            <button onClick={() => startReview("mistakes")} className="px-3 py-1 rounded-md border">
              Review Mistakes ({mistakes.length})
            </button>
          )}
          {username && username !== "guest" && starredQuestions.length > 0 && !reviewMode && (
            <button onClick={() => startReview("starred")} className="px-3 py-1 rounded-md border">
              Starred ({starredQuestions.length})
            </button>
          )}

          {(selectedCategory || reviewMode) && (
            <button onClick={reviewMode ? exitReview : backToMenu} className="px-3 py-1 rounded-md border">
              {reviewMode ? "Exit Review" : "Back"}
            </button>
          )}
        </div>

        {selectedCategory === "learned" && !reviewMode && geminiError && (
          <div className="bg-white rounded-xl p-3 shadow-sm mb-4 text-sm text-red-600">
            {geminiError}
          </div>
        )}

        {generatingEssay && selectedCategory === "learned" && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-4 text-sm text-gray-600">
            {geminiConnected
              ? t("Generating a customised short-answer question with Gemini...", "正在用 Gemini 生成定制简答题...")
              : t("Generating the next question...", "正在生成下一题...")}
          </div>
        )}

        {/* Country selection */}
        {selectedCategory && selectedCategory !== "learned" && !selectedCountry && !reviewMode && (
          <div className="flex flex-col space-y-3 mb-6">
            {Object.keys(selectedCategory === "country" ? countryMenu : grapeMenu).sort((a, b) => a.localeCompare(b)).map((name) => (
              <button
                key={name}
                onClick={() => setSelectedCountry(name)}
                className="p-4 rounded-lg text-white font-medium"
                style={{ background: "#F7C6D4" }}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Region selection */}
        {selectedCategory === "country" && selectedCountry && countryMenu[selectedCountry]?.length > 0 && !selectedRegion && !reviewMode && (
          <div className="flex flex-col space-y-3 mb-6">
            {countryMenu[selectedCountry].map((r) => (
              <button
                key={r}
                onClick={() => setSelectedRegion(r)}
                className="p-4 rounded-lg text-white font-medium"
                style={{ background: "#F4D19B" }}
              >
                {r}
              </button>
            ))}
            <button onClick={() => setSelectedCountry(null)} className="p-4 rounded-lg border bg-white text-gray-700">Back</button>
          </div>
        )}

        {/* Mistake review */}
        {reviewMode === "mistakes" && mistakes.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <h2 className="font-semibold mb-3">Your Mistakes</h2>
            <div className="space-y-3">
              {mistakes.map((m, i) => (
                <div key={i} className="p-3 border rounded-md">
                  <div className="flex justify-between items-start gap-2">
                    <div className="font-medium">{m.question.question[language]}</div>
                    <button
                      onClick={() => removeMistake(i)}
                      className="shrink-0 text-sm px-2 py-1 rounded-md border text-red-600 hover:bg-red-50"
                    >
                      {t("Remove", "删除")}
                    </button>
                  </div>
                  <div className="text-sm text-red-600 mt-1">
                    Correct: {m.question.answers.find(a => a.correct).text[language]}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    You chose: {m.selectedAnswer.text[language]}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <button onClick={exitReview} className="px-3 py-1 rounded-md border">Back</button>
            </div>
          </div>
        )}

        {/* Starred review */}
        {reviewMode === "starred" && starredQuestions.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <h2 className="font-semibold mb-3">Starred Questions</h2>
            <div className="space-y-3">
              {Object.values(questionFiles)
                .flatMap((c) => (Array.isArray(c) ? c : Object.values(c).flat()))
                .filter((q) => starredQuestions.includes(q.question.en))
                .map((q, i) => (
                  <div key={i} className="p-3 border rounded-md">
                    <div className="font-medium">{q.question[language]}</div>
                    <div className="text-sm text-green-700 mt-1">
                      Correct: {q.answers.find(a => a.correct).text[language]}
                    </div>
                  </div>
                ))}
            </div>
            <div className="mt-4">
              <button onClick={exitReview} className="px-3 py-1 rounded-md border">Back</button>
            </div>
          </div>
        )}

        {/* Revision set is full — remove confident questions */}
        {selectedCategory === "learned" && learnedNeedsCull && !reviewMode && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <h2 className="font-semibold mb-2">
              {t("Revision set is full (10/10)", "复习组已满（10/10）")}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {t(
                "Remove questions you are confident with. A new customised question can be generated after you make space.",
                "请先移除你已经有把握的题目。腾出空位后，才会生成下一道定制题。"
              )}
            </p>
            <div className="space-y-3">
              {questions.map((q) => (
                <div key={q.id} className="p-3 border rounded-md">
                  <div className="text-xs text-gray-500 mb-1">
                    {q.topic?.[language] || ""}
                    {q.origin === "mistake"
                      ? t(" · From mistakes", " · 来自错题")
                      : q.origin === "starred"
                        ? t(" · From starred", " · 来自收藏")
                        : t(" · Practice", " · 练习")}
                  </div>
                  <div className="font-medium mb-2">{q.question[language]}</div>
                  <button
                    onClick={() => removeLearnedQuestion(q.id)}
                    className="text-sm px-2 py-1 rounded-md border text-green-700 hover:bg-green-50"
                  >
                    {t("I'm confident — remove", "已经掌握 — 移除")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Short-answer card */}
        {currentQuestion?.type === "essay" && !reviewMode && !learnedNeedsCull && (
          <div className="mb-6">
            <div className="mb-3">
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${Math.round((questions.length / LEARNED_LIMIT) * 100)}%`,
                    background: "linear-gradient(90deg,#F7C6D4,#F4D19B)",
                  }}
                />
              </div>
              <div className="text-sm text-gray-600 mt-2">
                {t("Revision set", "复习组")}: {questions.length}/{LEARNED_LIMIT}
                {" · "}
                {t("Question", "第")} {Math.max(1, questions.findIndex((q) => q.id === currentQuestion.id) + 1)}
                /{questions.length}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-sm text-gray-500 mb-2">
                {t("Short-answer question", "简答题")}
                {currentQuestion.topic ? ` · ${currentQuestion.topic[language]}` : ""}
              </div>
              <div className="text-xs mb-3" style={{ color: "#C86B85" }}>
                {currentQuestion.origin === "mistake"
                  ? t("Customised from your mistakes", "根据你的错题生成")
                  : currentQuestion.origin === "starred"
                    ? t("Customised from your starred questions", "根据你的收藏题生成")
                    : t("Practice question", "练习题")}
                {currentQuestion.ai === "gemini" ? t(" · Gemini", " · Gemini") : ""}
              </div>
              <h2 className="text-lg md:text-xl font-semibold mb-3">
                {currentQuestion.question[language]}
              </h2>
              <p className="text-sm text-gray-500 mb-3">
                {t(
                  "Write a short structured answer in bullet points, then compare with the model answer.",
                  "请用要点形式写出简短结构化答案，再对照标准答案。"
                )}
              </p>
              <textarea
                value={essayDrafts[currentQuestion.id] || ""}
                onChange={(e) =>
                  setEssayDrafts((prev) => ({ ...prev, [currentQuestion.id]: e.target.value }))
                }
                rows={8}
                className="w-full p-3 rounded-md border text-sm mb-4"
                placeholder={t("Type your bullet-point answer here...", "在此用要点写下你的答案...")}
              />

              {!isFlipped && (
                <button
                  onClick={handleViewAnswer}
                  className="px-3 py-1 rounded-md bg-rose-400 text-white"
                >
                  {t("View Model Answer", "查看标准答案")}
                </button>
              )}

              {isFlipped && (
                <div className="mt-2 pt-4 border-t">
                  <h3 className="text-lg font-semibold mb-1">{t("Model answer", "标准答案")}</h3>
                  <div className="text-sm text-gray-500 mb-3">
                    {t("Key points", "参考要点")}
                  </div>
                  <ul className="list-disc pl-5 space-y-2 text-sm text-gray-800 mb-4">
                    {(currentQuestion.bullets || []).map((b, i) => (
                      <li key={i}>{b[language]}</li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      onClick={() => { setIsFlipped(false); setViewAnswerVisible(true); }}
                      className="px-3 py-2 rounded-md border"
                    >
                      {t("Hide Answer", "隐藏答案")}
                    </button>
                    <button onClick={previousQuestion} className="px-3 py-2 rounded-md border">
                      {t("Previous", "上一题")}
                    </button>
                    <button
                      onClick={() => removeLearnedQuestion(currentQuestion.id)}
                      className="px-3 py-2 rounded-md border text-green-700"
                    >
                      {t("I'm confident", "已经掌握")}
                    </button>
                    <button
                      onClick={nextQuestion}
                      disabled={generatingEssay}
                      className={`ml-auto px-4 py-2 rounded-md bg-rose-400 text-white ${generatingEssay ? "opacity-50" : ""}`}
                    >
                      {t("Next", "下一题")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quiz card */}
        {currentQuestion && currentQuestion.type !== "essay" && !reviewMode && (
          <div className="mb-6">
            {/* Progress bar */}
            <div className="mb-3">
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${progressPercentage}%`,
                    background: "linear-gradient(90deg,#F7C6D4,#F4D19B)",
                  }}
                />
              </div>
              <div className="text-sm text-gray-600 mt-2">
                {t("Progress", "进度")}: {questionsAnswered}/{totalQuestions}
              </div>
            </div>

            {/* Card flip container */}
            <div className="relative perspective-1000">
              <div className={`duration-700 transform-style preserve-3d ${isFlipped ? "rotate-y-180" : ""}`}>
                {/* Front of card */}
                <div className="card-front bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="text-sm text-gray-500">
                      {selectedCategory}: {selectedCountry} {selectedRegion ? `/ ${selectedRegion}` : ""}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleFindSimilar(currentQuestion)}
                        className="text-sm px-2 py-1 rounded-md border hover:bg-pink-50"
                      >
                        🔍 {t("Similar", "相似")}
                      </button>
                      <button
                        onClick={() => toggleStar(currentQuestion)}
                        disabled={username === "guest"}
                        className={`text-sm px-2 py-1 rounded-md border ${username === "guest" ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {isStarred(currentQuestion) ? "★" : "☆"}
                      </button>
                    </div>
                  </div>
                  <h2 className="text-lg md:text-xl font-semibold mb-4">
                    {currentQuestion.question[language]}
                  </h2>
                  <div className="flex flex-col gap-3">
                    {currentQuestion.answers.map((a, i) => (
                      <button
                        key={i}
                        onClick={() => selectAnswer(a)}
                        className={`p-3 rounded-md border transition ${
                          selectedAnswer === a ? "ring-2 ring-pink-200 bg-pink-50" : "bg-white hover:bg-pink-50"
                        }`}
                      >
                        {letters[i]}. {a.text[language]}
                      </button>
                    ))}
                  </div>
                  {viewAnswerVisible && (
                    <button
                      onClick={handleViewAnswer}
                      className="mt-4 px-3 py-1 rounded-md bg-rose-400 text-white"
                    >
                      {t("View Answer", "查看答案")}
                    </button>
                  )}
                </div>

                {/* Back of card (answer) */}
                {isFlipped && selectedAnswer && (
                  <div className="card-back rotate-y-180 bg-white rounded-2xl shadow-lg p-6 absolute left-0 top-0 w-full">
                    <h2 className="text-lg font-semibold mb-4">{t("Answer", "答案")}</h2>
                    <div className="text-green-700 font-medium mb-4">
                      {currentQuestion.answers.find(a => a.correct).text[language]}
                    </div>
                    <div className="mb-4">
                      <div className={`p-3 rounded-md ${selectedAnswer.correct ? "bg-green-50" : "bg-red-50"}`}>
                        <div className="font-medium">
                          {selectedAnswer.correct ? "You chose (Correct)" : "You chose (Incorrect)"}
                        </div>
                        <div className="text-sm mt-2">{selectedAnswer.explanation?.[language] || ""}</div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button onClick={() => setIsFlipped(false)} className="px-3 py-2 rounded-md border">Flip Back</button>
                      <button onClick={previousQuestion} className="px-3 py-2 rounded-md border">Previous</button>
                      <button onClick={nextQuestion} className="ml-auto px-4 py-2 rounded-md bg-rose-400 text-white">
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Similar questions popup */}
        {similarQuestions && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
            onClick={() => setSimilarQuestions(null)}
          >
            <div
              className="bg-white rounded-xl p-4 shadow-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-semibold">{t("Similar Questions", "相似题目")}</h2>
                <button
                  onClick={() => setSimilarQuestions(null)}
                  className="px-2 py-1 rounded-md border"
                >
                  ✕
                </button>
              </div>
              {similarQuestions.length === 0 ? (
                <div className="text-sm text-gray-500">
                  {t("No similar questions found.", "未找到相似题目。")}
                </div>
              ) : (
                <div className="space-y-3">
                  {similarQuestions.map((s, i) => (
                    <div key={i} className="p-3 border rounded-md">
                      <div className="font-medium">{s.q.question[language]}</div>
                      <div className="text-sm text-green-700 mt-1">
                        {t("Correct", "正确答案")}: {s.q.answers.find((a) => a.correct).text[language]}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;